// src/marketing/content/ContentCalendar.tsx
import { useState } from 'react'
import type { Content } from '../../lib/types'
import { KIND_LABELS } from './statuses'

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

function toIso(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * Date d'affichage d'un contenu : liveDate si renseignée, sinon plannedDate.
 * Divergence assumée avec la spec (« par planned_date, ou live_date si passé ») : on ne
 * compare pas à aujourd'hui — dès qu'une date effective existe, elle fait foi.
 */
export function displayDate(content: Content): string | null {
  return content.liveDate ?? content.plannedDate
}

interface ContentCalendarProps {
  contents: Content[]
  onDayClick: (isoDate: string) => void
  onContentClick: (id: string) => void
}

export function ContentCalendar({ contents, onDayClick, onContentClick }: ContentCalendarProps) {
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth())

  function prevMonth() {
    if (month === 0) { setYear(year - 1); setMonth(11) } else { setMonth(month - 1) }
  }
  function nextMonth() {
    if (month === 11) { setYear(year + 1); setMonth(0) } else { setMonth(month + 1) }
  }

  const byDate = new Map<string, Content[]>()
  for (const c of contents) {
    const d = displayDate(c)
    if (!d) continue
    const list = byDate.get(d) ?? []
    list.push(c)
    byDate.set(d, list)
  }

  const firstDay = new Date(year, month, 1)
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  // getDay() : 0=dim … 6=sam → décalage pour une semaine lun→dim
  const leadingBlanks = (firstDay.getDay() + 6) % 7
  const cells: (number | null)[] = [
    ...Array.from({ length: leadingBlanks }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]
  // Compléter la dernière semaine pour que la grille reste rectangulaire.
  const trailingBlanks = (7 - (cells.length % 7)) % 7
  cells.push(...Array.from({ length: trailingBlanks }, () => null))
  const todayIso = toIso(today.getFullYear(), today.getMonth(), today.getDate())

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <button type="button" onClick={prevMonth} aria-label="Mois précédent" style={navBtn}>←</button>
        <span
          data-testid="cal-month-label"
          style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--fg)', minWidth: '160px', textAlign: 'center' }}
        >
          {MONTHS[month]} {year}
        </span>
        <button type="button" onClick={nextMonth} aria-label="Mois suivant" style={navBtn}>→</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', background: 'var(--line)', border: '1px solid var(--line)', borderRadius: '10px', overflow: 'hidden' }}>
        {WEEKDAYS.map((d) => (
          <div key={d} style={{ background: 'var(--elevated)', padding: '0.375rem', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--dim)', textTransform: 'uppercase', textAlign: 'center' }}>
            {d}
          </div>
        ))}
        {cells.map((day, i) => {
          // var(--bg) : le token --panel n'existe pas dans src/theme.css.
          if (day === null) return <div key={`blank-${i}`} style={{ background: 'var(--bg)', minHeight: '90px' }} />
          const iso = toIso(year, month, day)
          const dayContents = byDate.get(iso) ?? []
          return (
            <div
              key={iso}
              data-testid={`cal-day-${iso}`}
              onClick={() => onDayClick(iso)}
              style={{
                background: 'var(--surface)', minHeight: '90px', padding: '0.25rem 0.375rem',
                cursor: 'pointer', outline: iso === todayIso ? '1px solid var(--arancio)' : 'none', outlineOffset: '-1px',
              }}
            >
              <span style={{ fontSize: '0.6875rem', color: iso === todayIso ? 'var(--arancio)' : 'var(--dim)', fontWeight: 600 }}>
                {day}
              </span>
              {dayContents.map((c) => (
                <div
                  key={c.id}
                  onClick={(e) => { e.stopPropagation(); onContentClick(c.id) }}
                  title={`${c.title} (${KIND_LABELS[c.kind]})`}
                  style={{
                    marginTop: '0.25rem', padding: '0.125rem 0.375rem', borderRadius: '4px',
                    background: c.kind === 'ad' ? 'rgba(217,83,30,0.15)' : 'var(--elevated)',
                    border: '1px solid var(--line)', fontSize: '0.6875rem',
                    color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {c.title}
                </div>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const navBtn: React.CSSProperties = {
  background: 'var(--elevated)',
  border: '1px solid var(--line)',
  borderRadius: '7px',
  padding: '0.25rem 0.625rem',
  color: 'var(--muted)',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
}
